import { POS_AUDIT_LABELS, POS_AUDIT_TONES } from '../constants/posConst';
import '../css/Pos.css';

/**
 * Turns a summary's field name into a readable label.
 *
 * The summaries are written as plain camelCase keys by posUtil (itemCount,
 * cashReceived), and a new POS action can add a field this component has
 * never heard of — so the label is derived rather than looked up in a map
 * that would silently fall back to a raw key.
 *
 * @param {string} key - e.g. 'itemCount'
 * @returns {string} e.g. 'Item count'
 */
const humaniseKey = (key) => String(key)
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/^./, c => c.toUpperCase());

/**
 * One summary value, rendered by its shape.
 *
 * Arrays get one line each rather than the comma-joined string the collapsed
 * row uses: the modal is where a long order's items are actually read, and a
 * run-on line is the thing the reader opened this to escape. Nested objects
 * (the 'before' snapshot on a product update) become their own labelled
 * list, so an edit can be compared field by field.
 *
 * @param {{value: *}} props
 * @returns {JSX.Element}
 */
const DetailValue = ({ value }) => {
  if (Array.isArray(value)) {
    return (
      <ul className="pos-audit-detail-lines">
        {value.map((line, i) => <li key={`${String(line)}-${i}`}>{String(line)}</li>)}
      </ul>
    );
  }

  if (value && typeof value === 'object') {
    return (
      <dl className="pos-audit-detail-nested">
        {Object.entries(value).map(([key, nested]) => (
          <div key={key}>
            <dt>{humaniseKey(key)}</dt>
            <dd>{String(nested)}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return <span>{String(value)}</span>;
};

/**
 * The full record behind one audit trail row.
 *
 * The list rows are deliberately terse — they exist to be scanned — so this
 * is where the whole entry is laid out: who, when, and every field of the
 * summary in full.
 *
 * Closes on its own close button or the footer button only. A tap outside is
 * ignored on purpose: the trail sits behind it and a mis-tap while reading a
 * long order would drop the reader back to the list.
 *
 * @param {object} props
 * @param {object|null} props.entry - the audit entry to show; falsy renders nothing
 * @param {() => void} props.onClose - called by both close controls
 * @returns {JSX.Element|null}
 */
function PosAuditDetailModal({ entry, onClose }) {
  if (!entry) return null;

  const label = POS_AUDIT_LABELS[entry.action] || entry.action;
  const tone = POS_AUDIT_TONES[entry.action] || 'info';
  const summary = (entry.summary && typeof entry.summary === 'object') ? entry.summary : {};
  const fields = Object.entries(summary)
    .filter(([, value]) => value !== null && value !== undefined && value !== '');

  return (
    <div className="pos-modal-overlay">
      <div className="pos-modal pos-modal-audit" role="dialog" aria-modal="true" aria-label={`${label} details`}>
        <header className="pos-modal-head">
          <h3>{label}</h3>
          <button
            type="button"
            className="pos-modal-close pos-modal-close-text"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pos-modal-body">
          {/* Who and when first: an audit entry is read to answer those two
              questions before anything about what changed. */}
          <dl className={`pos-audit-detail tone-${tone}`}>
            <div>
              <dt>When</dt>
              <dd>
                {new Date(entry.timestamp).toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'medium' })}
              </dd>
            </div>
            <div>
              <dt>By</dt>
              <dd>{entry.performedBy || 'Unknown'}</dd>
            </div>
            {fields.map(([key, value]) => (
              <div key={key}>
                <dt>{humaniseKey(key)}</dt>
                <dd><DetailValue value={value} /></dd>
              </div>
            ))}
          </dl>

          {/* The Firebase key. Shown last and set small — it is only ever
              needed when an entry has to be quoted to someone else. */}
          <p className="pos-audit-detail-id">Reference {entry.id}</p>
        </div>

        <footer className="pos-modal-foot">
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-primary" onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>
  );
}

export default PosAuditDetailModal;
